import { message } from 'odc-shared/components';
import { composeTrigValidate, useValidateHelper } from 'odc-shared/hooks/useValidateHelper';
import { checkEmail, checkURL } from 'odc-shared/utils/common';
import { useCallback, useMemo, useState } from 'react';

import { postContactUsSubmit } from '@src/api/generated/ContactUs';
import { CommPlatform, ModuleSubmitContactUsReq } from '@src/api/generated/data-contracts';
import { SET_GET_IN_TOUCH } from '@src/constants/common';

export interface ContactUsFormData {
  orgName: string;
  contactName: string;
  emailAddress: string;
  appUrl: string;
  helpType: CommPlatform[];
}

const REQUEST_FOR_OVERCLOCKING_CODE = 170001;

export const useContactUs = () => {
  // 表单状态
  const [formData, setFormData] = useState<ContactUsFormData>({
    orgName: '',
    contactName: '',
    emailAddress: '',
    appUrl: '',
    helpType: [],
  });

  // 提交状态
  const [loading, setLoading] = useState(false);
  const emailValidateHelper = useValidateHelper(formData.emailAddress.trim(), [
    (value: string) => checkEmail(value) ? true : 'Please enter a valid email address.',
  ]);
  const appUrlValidateHelper = useValidateHelper(formData.appUrl.trim(), [
    (value: string) => checkURL(value) ? true : 'Please enter a valid url.',
  ]);

  const [hasSubmitted, setHasSubmitted] = useState(() => {
    if (localStorage.getItem(SET_GET_IN_TOUCH) === 'true') {
      return true;
    }
    return false;
  });

  // 更新单个字段
  const updateField = useCallback(<K extends keyof ContactUsFormData>(
    field: K,
    value: ContactUsFormData[K],
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // 提交表单
  const submitForm = useCallback(async () => {
    const validateResult = composeTrigValidate(
      emailValidateHelper.trigValidate,
      appUrlValidateHelper.trigValidate,
    );
    if (!validateResult.isValid) {
      return;
    }
    setLoading(true);

    try {
      const requestData: ModuleSubmitContactUsReq = {
        companyName: formData.orgName || undefined,
        contactName: formData.contactName || undefined,
        contactEmail: formData.emailAddress || undefined,
        website: formData.appUrl,
        intentionType: formData.helpType,
      };

      const { code } = await postContactUsSubmit(requestData);

      if (code === 0) {
        message.success('Information submitted successfully.');
        setHasSubmitted(true);
        localStorage.setItem(SET_GET_IN_TOUCH, 'true');
      } else if (code === REQUEST_FOR_OVERCLOCKING_CODE) {
        message.error('You\'re submitting too frequently, please wait and try again.');
      } else {
        message.error('Information submission failed, please try again. ');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, emailValidateHelper, appUrlValidateHelper]);

  const submitDisable = useMemo(() => {
    if (!formData.orgName || !formData.contactName || !formData.emailAddress || !formData.appUrl || !formData.helpType.length) {
      return true;
    }
    const validateResult = composeTrigValidate(
      emailValidateHelper.trigValidate,
      appUrlValidateHelper.trigValidate,
    );
    if (!validateResult.isValid) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  return {
    formData,
    loading,
    submitDisable,
    emailValidateHelper,
    appUrlValidateHelper,
    hasSubmitted,
    updateField,
    submitForm,
  };
};

export default useContactUs;
